import type { Money } from '@dastarhan/contracts';

/**
 * Минуты по явкам: закрытые интервалы целиком, открытая — до «сейчас».
 * Кривые данные (уход раньше прихода) дают ноль, а не минус.
 */
/**
 * Открытая явка, про которую забыли, копит часы бесконечно:
 * ушёл в пятницу не отметившись — в понедельник «отработал» 72 часа
 * и получил их к выплате. Обрезаем открытый интервал шестнадцатью
 * часами: дольше человек не работает, а расхождение видно как
 * незакрытая явка и правится руками.
 */
export const OPEN_SHIFT_CAP_MIN = 16 * 60;

export function workedMinutes(
  intervals: { clockIn: Date; clockOut: Date | null }[],
  now: Date,
): number {
  let ms = 0;
  for (const i of intervals) {
    if (i.clockOut) {
      ms += Math.max(0, i.clockOut.getTime() - i.clockIn.getTime());
    } else {
      const open = Math.max(0, now.getTime() - i.clockIn.getTime());
      ms += Math.min(open, OPEN_SHIFT_CAP_MIN * 60_000);
    }
  }
  return Math.floor(ms / 60_000);
}

/** К выплате: минуты × ставка/60, округление один раз в конце. */
export function payFor(minutes: number, hourlyRate: Money): Money {
  return Math.round((minutes * hourlyRate) / 60);
}

// ─────────────────────────────────────────── процент от продаж

export type StaffRates = {
  /** ставка за час в тиынах; 0 — сотрудник только на проценте */
  hourlyRate: Money;
  /** процент от продаж в сотых долях: 250 = 2,5% */
  salesPctBp: number;
  /** достаётся ли официанту сбор за обслуживание с его столов */
  getsService: boolean;
};

export type StaffWork = {
  /** отработано минут за период */
  minutes: number;
  /** выручка по ЕГО чекам, без отменённых и возвратов */
  sales: Money;
  /** сбор за обслуживание по его чекам */
  service: Money;
  /** число закрытых чеков — по нему считается средний */
  checks: number;
};

export type Payslip = {
  byHours: Money;
  byPercent: Money;
  byService: Money;
  total: Money;
  /** часы с одним знаком: 7,5 */
  hours: number;
  /** средний чек: видно, кто продаёт, а кто разносит */
  avgCheck: Money;
};

/**
 * Зарплата официанта из трёх частей, и каждая отвечает за своё.
 *
 * ЧАСЫ — за присутствие. Человек пришёл, стоял в зале, носил блюда:
 * это оплачивается независимо от выручки, иначе в тихий вторник
 * он работает бесплатно.
 *
 * ПРОЦЕНТ — за старание. Предложил десерт, довёл компанию до второго
 * графина. Считается от ЕГО продаж, а не от общей выручки: иначе
 * один тянет, другой получает.
 *
 * ОБСЛУЖИВАНИЕ — решение владельца. В части кафе сбор целиком уходит
 * залу, в части остаётся заведению. Это не вопрос расчёта.
 *
 * Отменённые и возвращённые чеки в продажи не входят: продажи не было.
 */
export function payslipFor(rates: StaffRates, work: StaffWork): Payslip {
  const byHours = payFor(work.minutes, rates.hourlyRate);
  // Сотые доли: 10 000 = 100%. Округляем вниз — процент считается
  // от чужих денег, и лишний тиын лучше оставить заведению
  const byPercent = Math.floor((work.sales * rates.salesPctBp) / 10_000);
  const byService = rates.getsService ? work.service : 0;

  return {
    byHours,
    byPercent,
    byService,
    total: byHours + byPercent + byService,
    hours: Math.round((work.minutes / 60) * 10) / 10,
    avgCheck: work.checks > 0 ? Math.round(work.sales / work.checks) : 0,
  };
}

/** Процент для показа: 250 → «2,5%», 300 → «3%». */
export function pctLabel(bp: number): string {
  const v = bp / 100;
  return Number.isInteger(v) ? `${v}%` : `${String(v).replace('.', ',')}%`;
}

/**
 * Разбор ввода: «2,5» и «2.5» дают одинаковые 250 сотых.
 * Владелец печатает как привык, а не как удобно программе.
 */
export function pctToBp(input: string): number | null {
  const raw = input.trim().replace(',', '.').replace('%', '');
  if (raw === '') return 0;
  const v = Number(raw);
  if (!Number.isFinite(v) || v < 0 || v > 100) return null;
  return Math.round(v * 100);
}
